import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseQueryResult, UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { QData } from "../../../types/basic-type-list";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { IAdminOrder, IAdminOrderDetail, OrderStatusType, PaymentStatusType } from "../../../types/api/order";
import { getAdminOrderList, getAdminOrderDetail, updateOrderStatusApi, updateOrderPaymentStatusApi } from "./callbacks";

export const useAdminOrderList = (
  qData: QData & { paymentStatus?: string; orderStatus?: string },
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IAdminOrder[]>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponseWithQData<IAdminOrder[]>, Error> => {
  return useQuery<TResponseWithQData<IAdminOrder[]>, Error>({
    queryKey: [API_ENDPOINTS.ORDER_LIST, qData],
    queryFn: async () => {
      const res = await getAdminOrderList(qData);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: (res.data || []).map((x) => ({ key: x._id, ...x })),
    }),
    staleTime: 10000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useAdminOrderDetail = (
  id?: string,
  options?: Omit<
    UseQueryOptions<TResponse<IAdminOrderDetail>, Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<TResponse<IAdminOrderDetail>, Error> => {
  return useQuery<TResponse<IAdminOrderDetail>, Error>({
    queryKey: [API_ENDPOINTS.ORDER_DETAIL, id],
    queryFn: async () => {
      if (!id) throw new Error("Order ID is required");
      const res = await getAdminOrderDetail(id);
      return res.data;
    },
    enabled: !!id,
    staleTime: 5000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useUpdateOrderStatus = (
  options?: UseMutationOptions<
    TResponse<IAdminOrder>,
    Error,
    { id: string; status: OrderStatusType; comment?: string }
  >,
): UseMutationResult<
  TResponse<IAdminOrder>,
  Error,
  { id: string; status: OrderStatusType; comment?: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, comment }) => {
      const res = await updateOrderStatusApi(id, { status, comment });
      return res.data;
    },
    onSuccess: (res: any, ...rest: any[]) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ORDER_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ORDER_DETAIL, rest[0]?.id] });
      (options?.onSuccess as any)?.(res, ...rest);
    },
    ...options,
  });
};

export const useUpdateOrderPaymentStatus = (
  options?: UseMutationOptions<
    TResponse<IAdminOrder>,
    Error,
    { id: string; paymentStatus: PaymentStatusType }
  >,
): UseMutationResult<
  TResponse<IAdminOrder>,
  Error,
  { id: string; paymentStatus: PaymentStatusType }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, paymentStatus }) => {
      const res = await updateOrderPaymentStatusApi(id, { paymentStatus });
      return res.data;
    },
    onSuccess: (res: any, ...rest: any[]) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ORDER_LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.ORDER_DETAIL, rest[0]?.id] });
      (options?.onSuccess as any)?.(res, ...rest);
    },
    ...options,
  });
};
